import { Injectable } from '@angular/core';
import { AuthService } from '@cms_root/app/auth/base/services/auth.service';
import { Role } from '@cms_root/app/roles/models/role';
import { BehaviorSubject, of } from 'rxjs';
import { distinctUntilChanged, finalize, map, skipWhile, switchMap, tap } from 'rxjs/operators';
import { User } from '../models/user.model';
import { UserApiService } from './user-api.service';

interface UserState {
  user: User;
  roles: Role[];
  isLoading: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private readonly _initialState: UserState = { user: null, roles: [], isLoading: false };
  private _store$: BehaviorSubject<UserState> = new BehaviorSubject<UserState>(this._initialState);
  private _isUserDataLoaded: boolean = false;

  public readonly isLoading$ = this._store$.pipe(
    distinctUntilChanged(),
    map((state) => state?.isLoading)
  );
  public readonly user$ = this._store$.pipe(
    skipWhile(() => !this._isUserDataLoaded),
    distinctUntilChanged(),
    map((state) => state?.user)
  );
  public readonly roles$ = this._store$.pipe(
    skipWhile(() => !this._isUserDataLoaded),
    distinctUntilChanged(),
    map((state) => state?.roles)
  );

  public get roles(): Role[] {
    return [...this._store$.getValue()?.roles];
  }

  constructor(private readonly authService: AuthService, private readonly userApiService: UserApiService) {}

  public init(): void {
    this.authService.isAuth$
      .pipe(
        tap(() => this.updateState({ isLoading: true, user: null, roles: [] })),
        switchMap((isAuth) =>
          isAuth
            ? this.userApiService.getCurrentUser().pipe(
                switchMap((user) => this.userApiService.getUserRoles(user.id).pipe(map((roles) => ({ user, roles })))),
                finalize(() => {
                  this._isUserDataLoaded = true;
                  this.updateState({ isLoading: false });
                })
              )
            : of({ user: null, roles: [] })
        )
      )
      .subscribe(({ user, roles }) => {
        this.updateState({ user, roles });
      });
  }

  private updateState(newState: Partial<UserState>): void {
    this._store$.next({ ...this._store$.getValue(), ...newState });
  }
}
